import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserProfileModal from './UserProfileModal';
import { useAvatar } from '../../hooks/useAvatar';
import { getDisplayName } from '../../utils/nameUtils';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';
import { useHelpdeskHomePath } from '../../hooks/useHelpdeskHomePath';
import { HelpdeskPreferencesModal } from '../Atendimento/HelpdeskPreferencesModal';
import { EllipsisVerticalIcon, Bars3Icon } from '@heroicons/react/24/outline';

const isSettingsPath = (pathname: string): boolean =>
  pathname.startsWith(helpdeskRoutes.settings);

interface AtendimentoNavbarProps {
  onOpenMobileNav: () => void;
}

export const AtendimentoNavbar: React.FC<AtendimentoNavbarProps> = ({ onOpenMobileNav }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { homePath } = useHelpdeskHomePath();
  const { avatarUrl } = useAvatar({
    userId: user?.id,
    avatarBase64: user?.avatarBase64,
  });
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState(false);
  const [isModuleMenuOpen, setIsModuleMenuOpen] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const navigate = useNavigate();

  const settingsActive = isSettingsPath(location.pathname);
  const atendimentoActive = !settingsActive;

  const handleSignOut = () => {
    signOut();
    navigate('/login');
  };

  const handleProfileClick = () => {
    setIsProfileMenuOpen(false);
    setIsUserProfileModalOpen(true);
  };

  const handlePreferencesClick = () => {
    setIsModuleMenuOpen(false);
    setIsPreferencesModalOpen(true);
  };

  const initials = user?.name
    ? (() => {
        const names = user.name.split(' ').filter(n => n.length > 0);
        if (names.length === 0)
        {
          return 'U';
        }
        if (names.length === 1)
        {
          return names[0][0].toUpperCase();
        }
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      })()
    : 'U';

  const tabClass = (active: boolean) =>
    `ml-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'bg-white text-blue-700'
        : 'text-blue-100 hover:bg-blue-700 hover:text-white'
    }`;

  return (
    <>
      <nav className="bg-blue-600 text-white shadow-sm flex-shrink-0">
        <div className="px-2 sm:px-3 lg:px-4">
          <div className="flex justify-between h-14 lg:h-16">
            <div className="flex items-center min-w-0">
              <button
                type="button"
                className="lg:hidden p-2 -ml-1 rounded-md hover:bg-blue-700"
                onClick={onOpenMobileNav}
                aria-label="Abrir menu"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <Link to={homePath} className="flex-shrink-0 flex items-center pl-1 min-w-0">
                <img className="h-8 w-auto" src="/know-logo.png" alt="Logo" />
                <span className="ml-2 font-bold text-lg lg:text-xl truncate">Baruk Know</span>
              </Link>
              <div className="hidden lg:ml-2 lg:flex lg:items-center">
                <Link
                  to={homePath}
                  className={tabClass(atendimentoActive)}
                  aria-current={atendimentoActive ? 'page' : undefined}
                >
                  Atendimento
                </Link>
                <Link
                  to={helpdeskRoutes.settings}
                  className={tabClass(settingsActive)}
                  aria-current={settingsActive ? 'page' : undefined}
                >
                  Configurações
                </Link>
              </div>
            </div>

            <div className="flex items-center space-x-3 pr-2">
              <Link
                to="/"
                className="hidden sm:inline-flex px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700"
              >
                Voltar para Aplicação
              </Link>
              <div className="relative">
                <button
                  onClick={() => setIsModuleMenuOpen(!isModuleMenuOpen)}
                  className="p-2 hover:bg-blue-700 rounded-md"
                  type="button"
                  aria-label="Menu do módulo"
                >
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </button>
                {isModuleMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 text-gray-800">
                    <Link
                      to="/"
                      className="sm:hidden block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={() => setIsModuleMenuOpen(false)}
                    >
                      Voltar para Aplicação
                    </Link>
                    <button
                      onClick={handlePreferencesClick}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      type="button"
                    >
                      Preferências do atendimento
                    </button>
                  </div>
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center gap-2 hover:bg-blue-700 px-2 py-1.5 rounded-md"
                  type="button"
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="h-8 w-8 rounded-full" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-blue-800 flex items-center justify-center">
                      <span className="text-white text-sm font-medium">{initials}</span>
                    </div>
                  )}
                  <span className="hidden lg:inline text-sm font-medium">{getDisplayName(user?.name)}</span>
                </button>
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 text-gray-800">
                    <button
                      onClick={handleProfileClick}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      type="button"
                    >
                      Meu Perfil
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      type="button"
                    >
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <UserProfileModal
        isOpen={isUserProfileModalOpen}
        onClose={() => setIsUserProfileModalOpen(false)}
      />
      <HelpdeskPreferencesModal
        isOpen={isPreferencesModalOpen}
        onClose={() => setIsPreferencesModalOpen(false)}
      />
    </>
  );
};
